import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { format } from 'date-fns';

const GeneratedReportsList = ({ reports, onPreview, loading }) => {
  const getReportTypeIcon = (type) => {
    const icons = {
      financial: 'DollarSign',
      membership: 'Users',
      operations: 'Activity',
      inventory: 'Package',
      custom: 'FileText'
    };
    return icons?.[type] || 'FileText';
  };

  const getReportTypeColor = (type) => {
    const colors = {
      financial: 'bg-success/10 text-success',
      membership: 'bg-primary/10 text-primary',
      operations: 'bg-accent/10 text-accent',
      inventory: 'bg-warning/10 text-warning',
      custom: 'bg-muted text-muted-foreground'
    };
    return colors?.[type] || 'bg-muted text-muted-foreground';
  };

  if (loading) {
    return (
      <div className="bg-card rounded-xl p-6 shadow-md border border-border">
        <div className="space-y-4">
          {[1, 2, 3]?.map(i => (
            <div key={i} className="animate-pulse">
              <div className="h-20 bg-muted rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (reports?.length === 0) {
    return (
      <div className="bg-card rounded-xl p-12 shadow-md border border-border text-center">
        <Icon name="History" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No Reports Generated Yet</h3>
        <p className="text-sm text-muted-foreground">Generate your first report from the templates tab</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl shadow-md border border-border overflow-hidden">
      <div className="p-4 border-b border-border">
        <h2 className="font-semibold text-foreground">Generated Reports</h2>
        <p className="text-xs text-muted-foreground mt-1">{reports?.length} reports available</p>
      </div>
      <div className="divide-y divide-border">
        {reports?.map((report) => (
          <div key={report?.id} className="p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${getReportTypeColor(report?.report_type)}`}>
                <Icon name={getReportTypeIcon(report?.report_type)} size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h3 className="font-medium text-foreground">{report?.report_name}</h3>
                    <p className="text-xs text-muted-foreground mt-1">
                      Generated by {report?.generated_by_user?.full_name || 'Unknown'}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPreview(report)}
                    iconName="Eye"
                    iconPosition="left"
                  >
                    View
                  </Button>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Icon name="Calendar" size={14} />
                    <span>
                      {report?.date_range_start && report?.date_range_end
                        ? `${format(new Date(report.date_range_start), 'MMM d')} - ${format(new Date(report.date_range_end), 'MMM d, yyyy')}`
                        : 'Current data'
                      }
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon name="Clock" size={14} />
                    <span>{format(new Date(report?.created_at), 'MMM d, yyyy h:mm a')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GeneratedReportsList;